import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Leaf } from 'lucide-react';
import { companyInfo, productCategories } from '../mockData';
import { buildEmailLink } from '../lib/email';
import { SocialLinks } from './SocialLinks';
import { useLanguage } from '../i18n/LanguageContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const quickLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/solutions', label: t('nav.solutions') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Eco-Friendly Badge Section */}
      <div className="bg-emerald-600 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-white">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-3 rounded-full">
                <Leaf className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold">{t('footer.ecoTitle')}</h3>
                <p className="text-sm text-emerald-50">{t('footer.ecoSub')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_vbglobal-portal/artifacts/k1dc2y0l_VB%20GLOBAL%20LOGO.jpg"
              alt="VB Global Logo"
              className="h-16 w-auto mb-4 bg-white p-2 rounded"
            />
            <p className="text-sm mb-4">{companyInfo.description}</p>
            <SocialLinks variant="dark" />
          </div>

          <div>
            <h3 className="text-white font-semibold text-lg mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              {quickLinks.map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="hover:text-[#D4AF37] transition-colors text-sm">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold text-lg mb-4">{t('footer.ourProducts')}</h3>
            <ul className="space-y-2">
              {productCategories.map(c => (
                <li key={c.slug}>
                  <Link to={`/products/${c.slug}`} data-testid={`footer-link-${c.slug}`} className="hover:text-[#D4AF37] transition-colors text-sm">
                    {t(`categories.${c.slug}.name`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold text-lg mb-4">{t('footer.contactUs')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <a href={buildEmailLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-email-link" className="hover:text-[#D4AF37] transition-colors text-sm">{companyInfo.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <a href={`tel:${companyInfo.phone}`} className="hover:text-[#D4AF37] transition-colors text-sm">{companyInfo.phone}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{companyInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">© {currentYear} VB Global. {t('footer.rights')}</p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="hover:text-[#D4AF37] transition-colors">{t('footer.privacy')}</a>
              <a href="#" className="hover:text-[#D4AF37] transition-colors">{t('footer.terms')}</a>
              <Link to="/admin/login" data-testid="footer-admin-link" className="text-gray-500 hover:text-[#D4AF37] transition-colors">{t('footer.admin')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
